import React, { lazy } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';

import RootRoute from './RootRoute';
import POHome from '../pages/POHome';
import PoPublicRoutes from './PoPublicRoutes';
import OpenApiManagementPage from '../pages/po/system/OpenApiManagementPage';
import POMainPage from '../pages/POMainPage';

const ClientServerErrorPage = lazy(() => import('../pages/error/ClientServerErrorPage'));
const UnauthorizedErrorPage = lazy(() => import('../pages/error/UnauthorizedErrorPage'));
const NotFoundErrorPage = lazy(() => import('../pages/error/NotFoundErrorPage'));

const GoodManagementPage = lazy(() => import('src/pages/po/good/info/PoGoodManagementPage'));
const GoodRegistrationPage = lazy(() => import('src/pages/po/good/info/PoGoodRegistrationPage'));
const GroupGoodRegistrationPage = lazy(() => import('src/pages/po/good/info/PoGroupGoodRegistrationPage'));
const PoItemManagementPage = lazy(() => import('src/pages/po/good/info/PoItemManagementPage'));
const GoodRegistrationBatchPage = lazy(() => import('src/pages/po/good/info/PoBatchGoodRegistrationPage'));
const BrandPage = lazy(() => import('src/pages/po/good/base/PoBrandPage'));
const PoGoodApprovalHistoryPage = lazy(() => import('src/pages/po/good/approval/PoGoodApprovalHistoryPage'));
const PoTicketGoodManagementPage = lazy(() => import('src/pages/po/good/info/PoTicketGoodManagementPage'));
const PoTicketModificationPage = lazy(() => import('@bo-pages/po/good/info/PoTicketModificationPage'));

const PoGoodDetailPage = lazy(() => import('src/pages/po/good/info/PoGoodDetailPage'));
const PoGoodModificationPage = lazy(() => import('src/pages/po/good/info/PoGoodModificationPage'));
const PoGroupGoodDetailPage = lazy(() => import('src/pages/po/good/info/PoGroupGoodDetailPage'));
const PoGroupGoodModificationPage = lazy(() => import('src/pages/po/good/info/PoGroupGoodModificationPage'));

const PoTempGoodDetailPage = lazy(() => import('src/pages/po/good/info/PoTempGoodDetailPage'));
const PoTempGoodModificationPage = lazy(() => import('src/pages/po/good/approval/PoTempGoodModificationPage'));
const PoTempGroupGoodDetailPage = lazy(() => import('src/pages/po/good/info/PoTempGroupGoodDetailPage'));
const PoTempGroupGoodModificationPage = lazy(() =>
  import('src/pages/po/good/approval/PoTempGroupGoodModificationPage')
);

const DeliveryManagementPage = lazy(() => import('src/pages/po/delivery/PoDeliveryManagementPage'));
const DeliveryDetailPage = lazy(() => import('src/pages/po/delivery/PoDeliveryDetailPage'));
const ExchangeDeliveryManagementPage = lazy(() => import('src/pages/po/delivery/PoExchangeDeliveryManagementPage'));
const TicketDeliveryManagementPage = lazy(() => import('../pages/po/delivery/PoTicketDeliveryManagementPage'));
const ParkPointOrderPage = lazy(() => import('../pages/po/delivery/PoParkPointOrderPage'));
const ParkPointUseCompletePage = lazy(() => import('../pages/po/delivery/PoParkPointUseCompletePage'));
const DelayOrderRegistrationPage = lazy(() => import('../pages/po/delivery/PoDelayOrderRegistrationPage'));
const ReturnDeliveryPage = lazy(() => import('../pages/po/delivery/PoReturnDeliveryPage'));
const GoodSettlementPage = lazy(() => import('../pages/po/settlement/PoGoodSettlementPage'));
const ManualSettlementPage = lazy(() => import('../pages/po/settlement/PoManualSettlementPage'));
const VendorSettlementPage = lazy(() => import('../pages/po/settlement/PoVendorSettlementPage'));
const VendorChargeSettlementPage = lazy(() => import('../pages/po/settlement/PoVendorChargeSettlementPage'));
const PartnerChangePersonalInfoPage = lazy(() => import('../pages/po/member/PartnerChangePersonalInfoPage'));
const PartnerVendorDetailPage = lazy(() => import('../pages/po/vendor/PartnerVendorDetailPage'));
const SalesPartnerStatusPage = lazy(() => import('../pages/po/statistics/sales/SalesPartnerStatusPage'));
const SalesGoodStatusPage = lazy(() => import('@bo-pages/po/statistics/sales/SalesGoodStatusPage'));
const NotificationPage = lazy(() => import('../pages/po/customerService/notification/PoNotificationPage'));
const NotificationDetailPage = lazy(() => import('../pages/po/customerService/notification/PoNotificationDetailPage'));
const VendorQuestManagementPage = lazy(() =>
  import('../pages/po/customerService/vendorQuest/PoVendorQuestManagementPage')
);
const VendorQuestDetailPage = lazy(() => import('../pages/po/customerService/vendorQuest/PoVendorQuestDetailPage'));

const OrderCancelManagementPage = lazy(() => import('@bo-pages/po/orderCancel/OrderCancelManagementPage'));

const PoRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route path="" element={<RootRoute />}>
      <Route path="" element={<POHome />} errorElement={<ClientServerErrorPage />}>
        <Route path="" element={<POMainPage />} />
        <Route path="partner">
          <Route path="delivery">
            <Route path="management" element={<DeliveryManagementPage />} />
            <Route path="management/:id" element={<DeliveryDetailPage />} />
            <Route path="ticket-management" element={<TicketDeliveryManagementPage />} />
            <Route path="park-point-order" element={<ParkPointOrderPage />} />
            <Route path="park-point-use-complete" element={<ParkPointUseCompletePage />} />
            <Route path="delay-order-registration" element={<DelayOrderRegistrationPage />} />
            <Route path="change">
              <Route path="management" element={<ExchangeDeliveryManagementPage />} />
            </Route>
            <Route path="return">
              <Route path="management" element={<ReturnDeliveryPage />} />
            </Route>
          </Route>
          <Route path="settlement">
            <Route path="good-settlement" element={<GoodSettlementPage />} />
            <Route path="manual-settlement" element={<ManualSettlementPage />} />
            <Route path="vendor-settlement" element={<VendorSettlementPage />} />
            <Route path="vendor-charge-settlement" element={<VendorChargeSettlementPage />} />
          </Route>
          <Route path="customer-service">
            <Route path="notification">
              <Route path="management" element={<NotificationPage />} />
              <Route path="management/:id" element={<NotificationDetailPage />} />
            </Route>
            <Route path="vendor-quest">
              <Route path="management" element={<VendorQuestManagementPage />} />
              <Route path="management/:id/:vendorQuestNumber" element={<VendorQuestDetailPage />} />
            </Route>
          </Route>

          <Route path="member">
            <Route path="change-personal-info" element={<PartnerChangePersonalInfoPage />} />
          </Route>
          <Route path="vendor">
            <Route path="vendor-management" element={<PartnerVendorDetailPage />} />
          </Route>
          <Route path="good">
            <Route path="base">
              <Route path="brand-management" element={<BrandPage />} />
            </Route>
            <Route path="info">
              <Route path="management" element={<GoodManagementPage />} />
              <Route path="registration" element={<GoodRegistrationPage />} />
              <Route path="registration-group" element={<GroupGoodRegistrationPage />} />
              <Route path="registration-batch" element={<GoodRegistrationBatchPage />} />
              <Route path="temp-status/detail/:id" element={<PoTempGoodDetailPage />} />
              <Route path="temp-status/detail/:id/group" element={<PoTempGroupGoodDetailPage />} />
              <Route path="temp-status/modification/:id" element={<PoTempGoodModificationPage />} />
              <Route path="temp-status/modification/:id/group" element={<PoTempGroupGoodModificationPage />} />
              <Route path="approved-status/modification/:id" element={<PoGoodModificationPage />} />
              <Route path="approved-status/modification/:id/group" element={<PoGroupGoodModificationPage />} />
              <Route path="approved-status/detail/:id" element={<PoGoodDetailPage />} />
              <Route path="approved-status/detail/:id/group" element={<PoGroupGoodDetailPage />} />
              <Route path="item/management" element={<PoItemManagementPage />} />
              <Route path="ticket/management" element={<PoTicketGoodManagementPage />} />
              <Route path="ticket/modification/:id" element={<PoTicketModificationPage />} />
            </Route>
            <Route path="approval">
              <Route path="history" element={<PoGoodApprovalHistoryPage />} />
            </Route>
          </Route>
          <Route path="statistics">
            <Route path="sales">
              <Route path="partner-status" element={<SalesPartnerStatusPage />} />
              <Route path="good-status" element={<SalesGoodStatusPage />} />
            </Route>
          </Route>
          <Route path="system">
            <Route path="open-api-management" element={<OpenApiManagementPage />} />
          </Route>
          <Route path="order-cancel">
            <Route path="order-cancel-management" element={<OrderCancelManagementPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="partner/*" element={<PoPublicRoutes />} />
      <Route path="unauthorized" element={<UnauthorizedErrorPage />} />
      <Route path="not-found" element={<NotFoundErrorPage />} />
      <Route path="error" element={<ClientServerErrorPage />} />
      <Route path="*" element={<NotFoundErrorPage />} />
    </Route>
  )
);

export default PoRoutes;
